import React from "react";
import {
  QueryClientProvider,
  QueryClient,
  QueryClientConfig,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { DataContextProvider } from "@contexts/data";
import { AuthContextProvider } from "@contexts/auth";
import {
  IDataContextProvider,
  IDataMultipleContextProvider,
  IRouterProvider,
  ILayoutProps,
  INotificationProvider,
  IAuthProvider,
} from "@types";
import { ModirContextProvider } from "@contexts/modir";
import { RouterContextProvider } from "@contexts/router";
import { NotificationContextProvider } from "@contexts/notification";
import { TranslationContextProvider } from "@contexts/translation";
import { II18nProvider } from "@types";

export interface IModirProps {
  notificationProvider?: INotificationProvider | (() => INotificationProvider);
  authProvider?: IAuthProvider;
  dataProvider?: IDataContextProvider | IDataMultipleContextProvider;
  routerProvider: IRouterProvider;
  warnWhenUnsavedChanges: boolean;
  i18nProvider?: II18nProvider;
  LoginPage?: React.FC<React.PropsWithChildren>;
  DashboardPage?: React.FC<React.PropsWithChildren>;
  Layout?: React.FC<React.PropsWithChildren<ILayoutProps>>;
  Sider?: React.FC<React.PropsWithChildren>;
  Header?: React.FC<React.PropsWithChildren>;
  Footer?: React.FC<React.PropsWithChildren>;
  Title?: React.FC<React.PropsWithChildren>;
  reactQueryClientConfig?: QueryClientConfig;
  reactQueryDevtoolConfig?: any;
}

export const Modir: React.FC<React.PropsWithChildren<IModirProps>> = ({
  notificationProvider,
  authProvider,
  dataProvider,
  routerProvider,
  children,
  warnWhenUnsavedChanges,
  i18nProvider,
  LoginPage,
  DashboardPage,
  Layout,
  Sider,
  Header,
  Footer,
  Title,
  reactQueryClientConfig,
  reactQueryDevtoolConfig,
}) => {
  const queryClient = new QueryClient({
    ...reactQueryClientConfig,
    defaultOptions: {
      ...reactQueryClientConfig?.defaultOptions,
      queries: {
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        ...reactQueryClientConfig?.defaultOptions?.queries,
      },
    },
  });

  const notificationProviderContextValues =
    typeof notificationProvider === "function"
      ? notificationProvider()
      : notificationProvider ?? {};

  const { RouterComponent = React.Fragment } = routerProvider;

  return (
    <QueryClientProvider client={queryClient}>
      <NotificationContextProvider {...notificationProviderContextValues}>
        <AuthContextProvider
          {...(authProvider ?? {})}
          isProvided={Boolean(authProvider)}
        >
          <DataContextProvider {...dataProvider}>
            <RouterContextProvider {...routerProvider}>
              <TranslationContextProvider i18nProvider={i18nProvider}>
                <ModirContextProvider
                  warnWhenUnsavedChanges={warnWhenUnsavedChanges}
                  LoginPage={LoginPage}
                  DashboardPage={DashboardPage}
                  hasDashboard={!!DashboardPage}
                  Layout={Layout}
                  Sider={Sider}
                  Header={Header}
                  Footer={Footer}
                  Title={Title}
                >
                  <RouterComponent>{children}</RouterComponent>
                </ModirContextProvider>
              </TranslationContextProvider>
            </RouterContextProvider>
          </DataContextProvider>
        </AuthContextProvider>
      </NotificationContextProvider>
      <ReactQueryDevtools
        initialIsOpen={false}
        position="bottom-right"
        {...reactQueryDevtoolConfig}
      />
    </QueryClientProvider>
  );
};
